import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/checkout.css";

const FALLBACK_SHIPPING_RATE = 15;
const FALLBACK_FREE_SHIPPING_THRESHOLD = 250;

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  country: "United States",
  phone: "",
};

const parsePrice = (price) => {
  const value = Number(String(price ?? 0).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(value) ? value : 0;
};

const getShippingNumber = (keys, fallback) => {
  const shipping = siteConfig?.shipping || {};
  const value = keys.map((key) => shipping[key]).find((item) => item != null);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const shippingRate = getShippingNumber(
  ["flatRate", "standardRate", "rate", "cost"],
  FALLBACK_SHIPPING_RATE
);
const freeShippingThreshold = getShippingNumber(
  ["freeThreshold", "freeShippingThreshold", "threshold"],
  FALLBACK_FREE_SHIPPING_THRESHOLD
);
const canadaShippingRate = getShippingNumber(["canadaRate"], 28);
const internationalShippingRate = getShippingNumber(
  ["internationalRate"],
  45
);

const formatMoney = (value) =>
  new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
    style: "currency",
    currency: siteConfig.currency?.code || "USD",
  }).format(value);

const validate = (values) => {
  const nextErrors = {};

  if (!values.email.trim()) {
    nextErrors.email = "Enter your email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    nextErrors.email = "Enter a valid email address.";
  }
  if (!values.firstName.trim()) nextErrors.firstName = "Enter your first name.";
  if (!values.lastName.trim()) nextErrors.lastName = "Enter your last name.";
  if (!values.address.trim()) nextErrors.address = "Enter a street address.";
  if (!values.city.trim()) nextErrors.city = "Enter a city.";
  if (!values.region.trim()) nextErrors.region = "Enter a state or region.";
  if (!values.postalCode.trim()) {
    nextErrors.postalCode = "Enter a ZIP or postal code.";
  }
  if (!values.country) nextErrors.country = "Choose a country or region.";

  return nextErrors;
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fieldRefs = useRef({});
  const submitTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (submitTimerRef.current) {
        window.clearTimeout(submitTimerRef.current);
      }
    },
    []
  );

  const items = cartItems
    .map((item) => ({
      ...item,
      product: productData.find(
        (product) => String(product.id) === String(item.id)
      ),
    }))
    .filter((item) => item.product);
  const subtotal = items.reduce(
    (total, item) =>
      total + parsePrice(item.product.price) * Number(item.quantity || 0),
    0
  );
  const isDomestic = form.country === "United States";
  const shippingCost = isDomestic
    ? subtotal >= freeShippingThreshold
      ? 0
      : shippingRate
    : form.country === "Canada"
      ? canadaShippingRate
      : internationalShippingRate;
  const shippingEstimate = isDomestic
    ? "Estimated delivery in 4–7 business days"
    : form.country === "Canada"
      ? "Estimated delivery in 6–12 business days"
      : "Estimated delivery in 7–18 business days";
  const total = subtotal + shippingCost;

  const setFieldRef = (name) => (element) => {
    fieldRefs.current[name] = element;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting || !items.length) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      const firstInvalidField = Object.keys(nextErrors)[0];
      fieldRefs.current[firstInvalidField]?.focus();
      return;
    }

    setIsSubmitting(true);
    submitTimerRef.current = window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderTotal: total },
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 650);
  };

  const errorProps = (name) => ({
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  if (!items.length) {
    return (
      <main className="checkout-page checkout-page--empty">
        <section aria-labelledby="checkout-empty-title">
          <p className="checkout-page__eyebrow">Checkout</p>
          <h1 id="checkout-empty-title">There is nothing to check out</h1>
          <p>Add an item to your cart before continuing to checkout.</p>
          <Link to="/shop" className="checkout-page__empty-link">
            Shop the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <div className="checkout-page__heading">
        <div>
          <p className="checkout-page__eyebrow">Secure checkout</p>
          <h1>Complete your order</h1>
        </div>
        <Link to="/cartpage">Return to cart</Link>
      </div>

      <div className="checkout-page__layout">
        <form
          className="checkout-form"
          onSubmit={handleSubmit}
          noValidate
          aria-busy={isSubmitting}
        >
          <section className="checkout-form__section" aria-labelledby="contact-heading">
            <div className="checkout-form__section-heading">
              <span>01</span>
              <h2 id="contact-heading">Contact</h2>
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-email">Email address</label>
              <input
                ref={setFieldRef("email")}
                id="checkout-email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                {...errorProps("email")}
              />
              {errors.email && (
                <p className="checkout-field__error" id="email-error" role="alert">
                  {errors.email}
                </p>
              )}
            </div>
          </section>

          <section className="checkout-form__section" aria-labelledby="delivery-heading">
            <div className="checkout-form__section-heading">
              <span>02</span>
              <h2 id="delivery-heading">Delivery address</h2>
            </div>

            <div className="checkout-fields checkout-fields--two">
              <div className="checkout-field">
                <label htmlFor="checkout-first-name">First name</label>
                <input
                  ref={setFieldRef("firstName")}
                  id="checkout-first-name"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={handleChange}
                  {...errorProps("firstName")}
                />
                {errors.firstName && (
                  <p
                    className="checkout-field__error"
                    id="firstName-error"
                    role="alert"
                  >
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-last-name">Last name</label>
                <input
                  ref={setFieldRef("lastName")}
                  id="checkout-last-name"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={handleChange}
                  {...errorProps("lastName")}
                />
                {errors.lastName && (
                  <p
                    className="checkout-field__error"
                    id="lastName-error"
                    role="alert"
                  >
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-company">Company (optional)</label>
              <input
                id="checkout-company"
                name="company"
                type="text"
                autoComplete="organization"
                value={form.company}
                onChange={handleChange}
              />
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-address">Street address</label>
              <input
                ref={setFieldRef("address")}
                id="checkout-address"
                name="address"
                type="text"
                autoComplete="address-line1"
                value={form.address}
                onChange={handleChange}
                {...errorProps("address")}
              />
              {errors.address && (
                <p className="checkout-field__error" id="address-error" role="alert">
                  {errors.address}
                </p>
              )}
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-apartment">
                Apartment, suite, etc. (optional)
              </label>
              <input
                id="checkout-apartment"
                name="apartment"
                type="text"
                autoComplete="address-line2"
                value={form.apartment}
                onChange={handleChange}
              />
            </div>

            <div className="checkout-fields checkout-fields--two">
              <div className="checkout-field">
                <label htmlFor="checkout-city">City</label>
                <input
                  ref={setFieldRef("city")}
                  id="checkout-city"
                  name="city"
                  type="text"
                  autoComplete="address-level2"
                  value={form.city}
                  onChange={handleChange}
                  {...errorProps("city")}
                />
                {errors.city && (
                  <p className="checkout-field__error" id="city-error" role="alert">
                    {errors.city}
                  </p>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-region">State / region</label>
                <input
                  ref={setFieldRef("region")}
                  id="checkout-region"
                  name="region"
                  type="text"
                  autoComplete="address-level1"
                  value={form.region}
                  onChange={handleChange}
                  {...errorProps("region")}
                />
                {errors.region && (
                  <p className="checkout-field__error" id="region-error" role="alert">
                    {errors.region}
                  </p>
                )}
              </div>
            </div>

            <div className="checkout-fields checkout-fields--two">
              <div className="checkout-field">
                <label htmlFor="checkout-postal-code">ZIP / postal code</label>
                <input
                  ref={setFieldRef("postalCode")}
                  id="checkout-postal-code"
                  name="postalCode"
                  type="text"
                  autoComplete="postal-code"
                  value={form.postalCode}
                  onChange={handleChange}
                  {...errorProps("postalCode")}
                />
                {errors.postalCode && (
                  <p
                    className="checkout-field__error"
                    id="postalCode-error"
                    role="alert"
                  >
                    {errors.postalCode}
                  </p>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  ref={setFieldRef("country")}
                  id="checkout-country"
                  name="country"
                  autoComplete="country-name"
                  value={form.country}
                  onChange={handleChange}
                  {...errorProps("country")}
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="France">France</option>
                  <option value="Germany">Germany</option>
                  <option value="Japan">Japan</option>
                  <option value="Other">Other</option>
                </select>
                {errors.country && (
                  <p className="checkout-field__error" id="country-error" role="alert">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>

            <div className="checkout-field">
              <label htmlFor="checkout-phone">Phone (optional)</label>
              <input
                id="checkout-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="checkout-form__section" aria-labelledby="shipping-heading">
            <div className="checkout-form__section-heading">
              <span>03</span>
              <h2 id="shipping-heading">Shipping method</h2>
            </div>
            <div className="checkout-method">
              <div>
                <strong>Standard tracked shipping</strong>
                <span>{shippingEstimate}</span>
              </div>
              <strong>{shippingCost === 0 ? "Free" : formatMoney(shippingCost)}</strong>
            </div>
          </section>

          <section className="checkout-form__section" aria-labelledby="payment-heading">
            <div className="checkout-form__section-heading">
              <span>04</span>
              <h2 id="payment-heading">Payment</h2>
            </div>
            <div className="checkout-payment-note">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>Demo payment</strong>
                <p>
                  This storefront does not process or retain card details. Submit
                  the form to simulate a successful order.
                </p>
              </div>
            </div>
          </section>

          <button
            type="submit"
            className="checkout-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Completing order…"
              : `Complete demo order — ${formatMoney(total)}`}
          </button>

          <p className="checkout-form__policy">
            By completing this demo order, you acknowledge our{" "}
            <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.
          </p>
        </form>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <h2 id="checkout-summary-title">Order summary</h2>

          <div className="checkout-summary__items">
            {items.map((item) => {
              const { product } = item;
              const image = product.images?.[0] || product.image;
              const itemKey = `${item.id}-${item.size || "default"}-${
                item.color || "default"
              }`;

              return (
                <article className="checkout-summary__item" key={itemKey}>
                  <div className="checkout-summary__image-wrap">
                    <img src={image} alt="" />
                    <span aria-label={`Quantity ${item.quantity}`}>
                      {item.quantity}
                    </span>
                  </div>
                  <div>
                    <h3>{product.name}</h3>
                    <p>
                      {[item.color, item.size].filter(Boolean).join(" / ")}
                    </p>
                  </div>
                  <strong>
                    {formatMoney(parsePrice(product.price) * item.quantity)}
                  </strong>
                </article>
              );
            })}
          </div>

          <dl className="checkout-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatMoney(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shippingCost === 0 ? "Free" : formatMoney(shippingCost)}</dd>
            </div>
            <div className="checkout-summary__total">
              <dt>Total</dt>
              <dd>
                <small>{siteConfig.currency?.code || "USD"}</small>{" "}
                {formatMoney(total)}
              </dd>
            </div>
          </dl>

          <Link to="/returns" className="checkout-summary__returns-link">
            Shipping & returns information
          </Link>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
